import { create } from 'zustand';
import { QuickActionTemplate } from '../services/apiClientV2';
import { logDebug, logError } from '../utils/logging';
import { trackEvent } from '../services/analyticsService';
import useMindspaceStore from './mindspaceStore';
import { useChatStore } from './chatStore';
import useAuthStore from './authStore';

// Types for guided flow state
export interface GuidedQuestion {
  text: string;
  canonical_key?: string;
  template_key?: string;
  tier?: number;
  is_canonical: boolean;
}

export interface FlowResponse {
  questionIndex: number;
  question: GuidedQuestion;
  userResponse: string;
  timestamp: string;
}

export interface ActiveFlow {
  template: QuickActionTemplate;
  currentQuestionIndex: number;
  responses: FlowResponse[];
  startedAt: string;
  isCompleted: boolean;
  contextCollected: Record<string, string>; // canonical_key -> content mapping
}

// Enhanced state management as per Task F3.1 requirements
export type FlowStatus = 'idle' | 'collecting' | 'finalizing' | 'completed';

interface GuidedFlowState {
  // Current flow state - Updated to match Task F3.1 requirements
  status: FlowStatus; // 'idle'|'collecting'|'finalizing'|'completed'
  activeFlow: ActiveFlow | null;
  
  // Task F3.1 requirements
  answers: Record<string, string>; // Key-value pairs of collected answers
  startTime: string | null; // ISO timestamp when flow started
  
  // Flow management
  isLoading: boolean;
  error: string | null;
  
  // Legacy support (will be removed in future iterations)
  flowState: FlowStatus; // Maps to status for backward compatibility
  collectedContext: Record<string, string>;
  
  // Actions
  startFlow: (template: QuickActionTemplate) => void;
  answerQuestion: (response: string) => void;
  nextQuestion: () => boolean; // Returns true if there are more questions
  pauseFlow: () => void;
  resumeFlow: () => void;
  interruptFlow: () => void;
  completeFlow: () => string; // Returns final prompt with context
  resetFlow: () => void;
  abandonFlow: () => void; // New action for abandoning the flow
  
  // Context management
  updateCollectedContext: (key: string, value: string) => void;
  getCollectedContext: () => Record<string, string>;
  
  // Flow navigation
  getCurrentQuestion: () => GuidedQuestion | null;
  getProgress: () => { current: number; total: number };
  canGoBack: () => boolean;
  goBack: () => void;
  
  // Session persistence (Task F3.1)
  saveToSession: () => void;
  loadFromSession: () => boolean;
  clearSession: () => void;
  
  // Utility
  clearError: () => void;
}

// Session storage key for persistence
const GUIDED_FLOW_SESSION_KEY = 'smartie_guided_flow_state';

// Helper functions for session storage
const saveFlowToSession = (state: Partial<GuidedFlowState>) => {
  try {
    const sessionData = {
      status: state.status,
      activeFlow: state.activeFlow,
      answers: state.answers,
      startTime: state.startTime,
      timestamp: new Date().toISOString()
    };
    sessionStorage.setItem(GUIDED_FLOW_SESSION_KEY, JSON.stringify(sessionData));
    logDebug('[guidedFlowStore] Saved state to sessionStorage', sessionData);
  } catch (error) {
    logError('[guidedFlowStore] Failed to save to sessionStorage:', error);
  }
};

const loadFlowFromSession = (): Partial<GuidedFlowState> | null => {
  try {
    const sessionData = sessionStorage.getItem(GUIDED_FLOW_SESSION_KEY);
    if (!sessionData) return null;
    
    const parsed = JSON.parse(sessionData);
    logDebug('[guidedFlowStore] Loaded state from sessionStorage', parsed);
    
    // Validate session data structure
    if (parsed.status && parsed.activeFlow && parsed.answers && parsed.startTime) {
      return {
        status: parsed.status,
        activeFlow: parsed.activeFlow,
        answers: parsed.answers,
        startTime: parsed.startTime,
        flowState: parsed.status, // Backward compatibility
        collectedContext: parsed.activeFlow.contextCollected || {}
      };
    }
    
    return null;
  } catch (error) {
    logError('[guidedFlowStore] Failed to load from sessionStorage:', error);
    return null;
  }
};

const clearFlowSession = () => {
  try {
    sessionStorage.removeItem(GUIDED_FLOW_SESSION_KEY);
    logDebug('[guidedFlowStore] Cleared sessionStorage');
  } catch (error) {
    logError('[guidedFlowStore] Failed to clear sessionStorage:', error);
  }
};

// Defensive helper to safely access template questions
const getTemplateQuestions = (template: QuickActionTemplate): GuidedQuestion[] => {
  const anyTemplate = template as unknown as { questions?: unknown };
  const qs = anyTemplate?.questions;
  return Array.isArray(qs) ? (qs as GuidedQuestion[]) : [];
};

export const useGuidedFlowStore = create<GuidedFlowState>((set, get) => {

  // Helper to create a new state object, ensuring immutability
  const mergeState = (partial: Partial<GuidedFlowState>) => {
    set((state) => ({ ...state, ...partial }));
  };

  return ({
    // Initial state - Updated for Task F3.1
    status: 'idle',
    activeFlow: null,
    answers: {},
    startTime: null,
    isLoading: false,
    error: null,
    
    // Legacy support
    flowState: 'idle',
    collectedContext: {},

    // Start a guided flow with a template
    startFlow: (template: QuickActionTemplate) => {
      logDebug('[guidedFlowStore] Starting guided flow for template:', template.template_id);
      
      const now = new Date().toISOString();
      
      const flow: ActiveFlow = {
        template,
        currentQuestionIndex: 0,
        responses: [],
        startedAt: now,
        isCompleted: false,
        contextCollected: {}
      };

      // Track flow start (F3.8)
      trackEvent('guided_flow_started', {
        template_id: template.template_id,
        template_title: template.title,
        template_category: template.category,
        card_type: template.card_type
      });

      // ------------------------------
      // Task T-4: Session-context pre-population (v0.1)
      // Pre-fill answers with existing Tier-1/Tier-2 canonical context values
      // ---------------------------------------------------------------
      const mindspaceState = useMindspaceStore.getState();

      const prefilledAnswers: Record<string, string> = {};
      const prefilledContext: Record<string, string> = {};

      if (mindspaceState?.contextItems?.length) {
        // Build a quick lookup map for existing context values (trimmed)
        const contextMap: Record<string, string> = {};
        mindspaceState.contextItems.forEach((item) => {
          if (item.content && item.content.trim().length > 0) {
            contextMap[item.item_type] = item.content.trim();
          }
        });

        // Iterate template questions and pre-fill when appropriate
        getTemplateQuestions(template).forEach((q) => {
          if (
            q.is_canonical &&
            q.canonical_key &&
            // Only Tier-1 / Tier-2 keys for v0.1 as per spec
            (q.tier === undefined || q.tier <= 2)
          ) {
            const existingValue = contextMap[q.canonical_key];
            if (existingValue) {
              prefilledAnswers[q.canonical_key] = existingValue;
              prefilledContext[q.canonical_key] = existingValue;
            }
          }
        });
      }

      // Merge any prefilled context into the newly created flow object
      if (Object.keys(prefilledContext).length > 0) {
        flow.contextCollected = { ...flow.contextCollected, ...prefilledContext };
      }

      const newState = {
        status: 'collecting' as FlowStatus,
        flowState: 'collecting' as FlowStatus, // Backward compatibility
        activeFlow: flow,
        // Prefilled answers (may be empty)
        answers: prefilledAnswers,
        startTime: now,
        error: null,
        // Prefilled collected context (may be empty)
        collectedContext: prefilledContext
      };

      mergeState(newState);
      
      // Save to session storage
      saveFlowToSession(newState);
    },

    // Answer current question and collect context
    answerQuestion: (response: string) => {
      const state = get();
      const { activeFlow } = state;
      
      if (!activeFlow || state.status !== 'collecting') {
        logError('[guidedFlowStore] Cannot answer question: no active flow or wrong status');
        return;
      }

      const currentQuestion = getTemplateQuestions(activeFlow.template)[activeFlow.currentQuestionIndex];
      if (!currentQuestion) {
        logError('[guidedFlowStore] Cannot answer question: no current question');
        return;
      }

      logDebug('[guidedFlowStore] Answering question:', currentQuestion.text, 'Response:', response);

      // Create response record
      const flowResponse: FlowResponse = {
        questionIndex: activeFlow.currentQuestionIndex,
        question: currentQuestion,
        userResponse: response.trim(),
        timestamp: new Date().toISOString()
      };

      // Update context if this is a canonical question
      const updatedContextCollected = { ...activeFlow.contextCollected };
      const updatedCollectedContext = { ...state.collectedContext };
      const updatedAnswers = { ...state.answers };
      
      // Store answer with appropriate key
      const answerKey = currentQuestion.is_canonical && currentQuestion.canonical_key 
        ? currentQuestion.canonical_key 
        : currentQuestion.template_key || `question_${activeFlow.currentQuestionIndex}`;
      
      updatedAnswers[answerKey] = response.trim();
      
      if (currentQuestion.is_canonical && currentQuestion.canonical_key) {
        updatedContextCollected[currentQuestion.canonical_key] = response.trim();
        updatedCollectedContext[currentQuestion.canonical_key] = response.trim();
      }

      // Update active flow
      const updatedFlow: ActiveFlow = {
        ...activeFlow,
        responses: [...activeFlow.responses, flowResponse],
        contextCollected: updatedContextCollected
      };

      // Track step completed (F3.8)
      trackEvent('guided_flow_step_completed', {
        template_id: activeFlow.template.template_id,
        step_number: activeFlow.currentQuestionIndex + 1, // 1-based index
        total_steps: getTemplateQuestions(activeFlow.template).length,
        question_key: currentQuestion.is_canonical && currentQuestion.canonical_key 
          ? currentQuestion.canonical_key 
          : (currentQuestion.template_key || `question_${activeFlow.currentQuestionIndex}`)
      });

      const newState = {
        activeFlow: updatedFlow,
        answers: updatedAnswers,
        collectedContext: updatedCollectedContext
      };

      mergeState(newState);
      
      // Save to session storage
      saveFlowToSession(newState);

      // Task F3.2: Automatically persist Tier-1 and Tier-2 canonical answers to backend
      const guestFlag = useAuthStore.getState().isGuest;

      if (
        currentQuestion.is_canonical &&
        currentQuestion.canonical_key &&
        (currentQuestion.tier === undefined || currentQuestion.tier <= 2)
        && !guestFlag
      ) {
        try {
          const mindspaceStore = useMindspaceStore.getState();
          // Optimistic UI already handled by guidedFlowStore state; mindspaceStore will update its own cache on success.
          void mindspaceStore
            .updateContextItem(currentQuestion.canonical_key, response.trim())
            .catch((err: unknown) => {
              logError('[guidedFlowStore] Failed to persist canonical context item:', err);
              // No rollback necessary here since failure does not modify mindspaceStore state.
            });
        } catch (err) {
          logError('[guidedFlowStore] Error accessing mindspaceStore for context persistence:', err);
        }
      }
    },

    // Move to next question
    nextQuestion: () => {
      const state = get();
      const { activeFlow } = state;
      
      if (!activeFlow || state.status !== 'collecting') {
        return false;
      }

      const nextIndex = activeFlow.currentQuestionIndex + 1;
      const hasMoreQuestions = nextIndex < getTemplateQuestions(activeFlow.template).length;

      if (hasMoreQuestions) {
        logDebug('[guidedFlowStore] Moving to next question:', nextIndex);
        
        const updatedFlow = {
          ...activeFlow,
          currentQuestionIndex: nextIndex
        };

        // Note: No analytics tracking for nextQuestion per F3.8 plan

        const newState = {
          activeFlow: updatedFlow
        };

        mergeState(newState);
        
        // Save to session storage
        saveFlowToSession(newState);

        return true;
      } else {
        logDebug('[guidedFlowStore] No more questions, flow ready for completion');
        
        // Transition to finalizing status
        const newState = {
          status: 'finalizing' as FlowStatus,
          flowState: 'finalizing' as FlowStatus
        };
        
        mergeState(newState);
        saveFlowToSession(newState);
        
        return false;
      }
    },

    // Pause the current flow
    pauseFlow: () => {
      const state = get();
      if (state.status === 'collecting') {
        logDebug('[guidedFlowStore] Pausing flow');
        
        // Note: No analytics tracking for pauseFlow per F3.8 plan

        // Note: We don't change status to 'paused' since Task F3.1 only specifies 4 states
        // Pausing is handled by not changing the status but allowing resume
        logDebug('[guidedFlowStore] Flow paused (status remains collecting for resume)');
      }
    },

    // Resume a paused flow
    resumeFlow: () => {
      const state = get();
      if (state.status === 'collecting' && state.activeFlow) {
        logDebug('[guidedFlowStore] Resuming flow');
        
        // Note: No analytics tracking for resumeFlow per F3.8 plan

        // Flow is already in collecting state, just log the resume
        logDebug('[guidedFlowStore] Flow resumed (already in collecting state)');
      }
    },

    // Interrupt the current flow (user wants to exit)
    interruptFlow: () => {
      const state = get();
      if (state.activeFlow && state.status !== 'idle') {
        logDebug('[guidedFlowStore] Interrupting flow');
        
        // Note: interruptFlow calls abandonFlow, which handles analytics per F3.8 plan

        // Reset to idle state and clear session
        mergeState({ 
          status: 'idle',
          flowState: 'idle',
          activeFlow: null,
          answers: {},
          startTime: null,
          collectedContext: {},
          error: null
        });
        
        clearFlowSession();
      }
    },

    // Complete the flow and generate final prompt
    completeFlow: () => {
      const state = get();
      const { activeFlow, answers } = state;
      
      if (!activeFlow) {
        throw new Error('No active flow to complete');
      }

      logDebug('[guidedFlowStore] Completing flow');

      const template = activeFlow.template.final_prompt_template || activeFlow.template.initial_prompt;
      
      // --- Task T-5: Final-prompt merge safeguard ---
      // 1. Get the latest full context from mindspaceStore
      const mindspaceState = useMindspaceStore.getState();
      const mindspaceContext: Record<string, string> = {};
      mindspaceState.contextItems.forEach(item => {
        mindspaceContext[item.item_type] = item.content;
      });

      // 2. Merge contexts: mindspace context first, then answers from the flow.
      //    This ensures answers from the current flow overwrite any existing context.
      const finalContext = { ...mindspaceContext, ...answers };
      
      // 3. Replace template variables with the merged context
      let finalPrompt = template;
      Object.entries(finalContext).forEach(([key, value]) => {
        // Use a global regex to replace all occurrences of the placeholder
        const placeholder = `{{${key}}}`;
        // Ensure value is a string to prevent 'replace of undefined' errors
        const replacementValue = typeof value === 'string' ? value : '';
        finalPrompt = finalPrompt.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacementValue);
      });

      // Calculate duration for completion tracking (F3.8)
      const durationMs = state.startTime 
        ? Date.now() - new Date(state.startTime).getTime()
        : 0;

      // Track flow completion (F3.8)
      trackEvent('guided_flow_completed', {
        template_id: activeFlow.template.template_id,
        total_duration_ms: durationMs
      });

      // Validate that no unresolved placeholders remain
      const unresolvedPlaceholderPattern = /\{\{\s*[^}]+\s*}}/g;
      if (unresolvedPlaceholderPattern.test(finalPrompt)) {
        const errorMsg = 'Unele variabile nu au fost completate în promptul final.';
        logError('[guidedFlowStore] ' + errorMsg, finalPrompt);
        mergeState({ error: errorMsg });
        throw new Error(errorMsg);
      }

      // Send the final prompt via chatStore using the unified endpoint.
      // Guest users do NOT have a mindspace, so we skip that requirement and
      // instead forward the collected answers as `session_context`.
      try {
        const isGuest = useAuthStore.getState().isGuest;

        if (isGuest) {
          // Build a session_context containing ONLY canonical keys collected in this flow
          const canonicalKeys = getTemplateQuestions(activeFlow.template)
            .filter((q) => q.is_canonical && q.canonical_key)
            .map((q) => q.canonical_key as string);

          const sessionContext: Record<string, string> = {};
          canonicalKeys.forEach((key) => {
            if (key && answers[key]) {
              sessionContext[key] = answers[key];
            }
          });

          useChatStore.getState().sendMessageWithMindspace(
            finalPrompt,
            '', // mindspaceId ignored for guests
            activeFlow.template.template_id,
            sessionContext,
          );
        } else {
          const mindspaceId = useMindspaceStore.getState().defaultMindspace?.id;
          if (!mindspaceId) {
            logError('[guidedFlowStore] Mindspace indisponibil pentru trimiterea promptului.');
            mergeState({ error: 'Contextul nu este disponibil în acest moment.' });
          } else {
            useChatStore.getState().sendMessageWithMindspace(
              finalPrompt,
              mindspaceId,
              activeFlow.template.template_id,
              {},
            );
          }
        }
      } catch (err) {
        logError('[guidedFlowStore] Eroare la trimiterea promptului final:', err);
        mergeState({ error: err instanceof Error ? err.message : String(err) });
      }

      // Mark flow as completed
      const newState = {
        status: 'completed' as FlowStatus,
        flowState: 'completed' as FlowStatus,
        activeFlow: {
          ...activeFlow,
          isCompleted: true
        }
      };

      mergeState(newState);
      
      // Save completed state to session
      saveFlowToSession(newState);

      return finalPrompt;
    },

    // Reset flow state
    resetFlow: () => {
      logDebug('[guidedFlowStore] Resetting flow state.');
      const initialState = {
        status: 'idle' as FlowStatus,
        flowState: 'idle' as FlowStatus,
        activeFlow: null,
        answers: {},
        startTime: null,
        error: null,
        collectedContext: {},
      };
      
      mergeState(initialState);
      clearFlowSession();
    },

    // Update collected context (for external context updates)
    updateCollectedContext: (key: string, value: string) => {
      const state = get();
      logDebug('[guidedFlowStore] Updating collected context:', key, value);
      
      const updatedContext = {
        ...state.collectedContext,
        [key]: value.trim()
      };

      const updatedAnswers = {
        ...state.answers,
        [key]: value.trim()
      };

      // Also update active flow if it exists
      let updatedFlow = state.activeFlow;
      if (updatedFlow) {
        updatedFlow = {
          ...updatedFlow,
          contextCollected: {
            ...updatedFlow.contextCollected,
            [key]: value.trim()
          }
        };
      }

      const newState = {
        collectedContext: updatedContext,
        answers: updatedAnswers,
        activeFlow: updatedFlow
      };

      mergeState(newState);
      
      // Save to session storage
      saveFlowToSession(newState);
    },

    // Get all collected context
    getCollectedContext: () => {
      const state = get();
      return state.collectedContext;
    },

    // Get current question
    getCurrentQuestion: () => {
      const state = get();
      const { activeFlow } = state;
      
      if (!activeFlow || state.status !== 'collecting') {
        return null;
      }

      return getTemplateQuestions(activeFlow.template)[activeFlow.currentQuestionIndex] || null;
    },

    // Get progress information
    getProgress: () => {
      const state = get();
      const { activeFlow } = state;
      
      if (!activeFlow) {
        return { current: 0, total: 0 };
      }

      const total = getTemplateQuestions(activeFlow.template).length;
      const current = total === 0 ? 0 : activeFlow.currentQuestionIndex + 1;
      return { current, total };
    },

    // Check if user can go back
    canGoBack: () => {
      const state = get();
      const { activeFlow } = state;
      
      return activeFlow ? activeFlow.currentQuestionIndex > 0 : false;
    },

    // Go back to previous question
    goBack: () => {
      const state = get();
      const { activeFlow } = state;
      
      if (!activeFlow || activeFlow.currentQuestionIndex <= 0) {
        return;
      }

      logDebug('[guidedFlowStore] Going back to previous question');
      
      const previousIndex = activeFlow.currentQuestionIndex - 1;
      
      // Remove the last response and update context
      const updatedResponses = activeFlow.responses.slice(0, -1);
      const updatedContextCollected = { ...activeFlow.contextCollected };
      const updatedCollectedContext = { ...state.collectedContext };
      const updatedAnswers = { ...state.answers };

      // Remove context from the question we're backing out of
      const currentQuestion = getTemplateQuestions(activeFlow.template)[activeFlow.currentQuestionIndex];
      if (currentQuestion?.is_canonical && currentQuestion.canonical_key) {
        delete updatedContextCollected[currentQuestion.canonical_key];
        delete updatedCollectedContext[currentQuestion.canonical_key];
        delete updatedAnswers[currentQuestion.canonical_key];
      }
      
      // Also remove template_key answer if present
      if (currentQuestion?.template_key) {
        delete updatedAnswers[currentQuestion.template_key];
      }

      // Note: No analytics tracking for goBack per F3.8 plan

      const newState = {
        activeFlow: {
          ...activeFlow,
          currentQuestionIndex: previousIndex,
          responses: updatedResponses,
          contextCollected: updatedContextCollected
        },
        answers: updatedAnswers,
        collectedContext: updatedCollectedContext
      };

      mergeState(newState);
      
      // Save to session storage
      saveFlowToSession(newState);
    },

    // Session persistence methods (Task F3.1)
    saveToSession: () => {
      const state = get();
      saveFlowToSession(state);
    },

    loadFromSession: () => {
      const sessionState = loadFlowFromSession();
      if (sessionState) {
        logDebug('[guidedFlowStore] Restoring state from session');
        mergeState(sessionState);
        return true;
      }
      return false;
    },

    clearSession: () => {
      clearFlowSession();
    },

    // Clear error state
    clearError: () => {
      mergeState({ error: null });
    },

    // New action to abandon the flow
    abandonFlow: () => {
      const state = get();
      if (state.activeFlow) {
        // Calculate duration for abandonment tracking (F3.8)
        const durationMs = state.startTime 
          ? Date.now() - new Date(state.startTime).getTime()
          : 0;

        trackEvent('guided_flow_abandoned', {
          template_id: state.activeFlow.template.template_id,
          last_completed_step: state.activeFlow.currentQuestionIndex, // 0-based, so this is the step they were on when abandoning
          duration_ms: durationMs
        });
      }
      logDebug('[guidedFlowStore] User abandoned guided flow.');
      get().resetFlow();
    },
  });
});

export default useGuidedFlowStore; 